import { useCallback } from 'react';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook } from '@atlassian/react-sweet-state';
import * as actions from './actions/index.tsx';
import { emptyServiceDataState } from './constants.tsx';
import { createInitialState, useCrossTabCache } from './cross-tab-cache/index.tsx';
import type { ActiveIncidentsState, ServiceDataLoadingState } from './types.tsx';

const CACHE_INVALIDATION_MS = 300000;

const Store = createStore({
	initialState: createInitialState(),
	actions,
	name: 'linked-object-incidents-store',
});

// Consumers of this package should not use this hook.
export const useActiveIncidentsStore = createHook(Store);

export const useFetchActiveIncidents = (fetchImmediate?: boolean) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useCrossTabCache();

	const [activeIncidents, { fetchActiveIncidents: doFetchActiveIncidents }] =
		useActiveIncidentsStore();

	// adding activeIncidents into fetchActiveIncidentsForServices dependencies created an infinite loop
	// FIXME: use refs
	// const activeIncidentsRef = useRef(activeIncidents);
	// activeIncidentsRef.current = activeIncidents;

	const fetchActiveIncidentsForServices = useCallback(
		(affectedServicesFieldId: string, serviceIds: string[]): void => {
			const currentTimeMs = Date.now();

			const serviceIdsToFetch = serviceIds.filter((serviceId) => {
				const { loading: alreadyFetchingForService, loadedAt } =
					activeIncidents[serviceId] ?? emptyServiceDataState;

				const isStale = currentTimeMs - loadedAt > CACHE_INVALIDATION_MS;
				return !alreadyFetchingForService && (isStale || fetchImmediate);
			});

			fireOperationalAnalytics(createAnalyticsEvent({}), 'useFetchActiveIncidents requested', {
				ttl: CACHE_INVALIDATION_MS,
				requestedServices: serviceIds.length,
			});

			if (serviceIdsToFetch.length > 0) {
				doFetchActiveIncidents(affectedServicesFieldId, serviceIdsToFetch);
				fireOperationalAnalytics(createAnalyticsEvent({}), 'useFetchActiveIncidents cacheMissed', {
					ttl: CACHE_INVALIDATION_MS,
					cacheMissedServices: serviceIdsToFetch.length,
				});
			}
		},
		[activeIncidents, createAnalyticsEvent, doFetchActiveIncidents, fetchImmediate],
	);

	const fetchActiveIncidentsForService = useCallback(
		(affectedServicesFieldId: string, serviceId: string): void =>
			fetchActiveIncidentsForServices(affectedServicesFieldId, [serviceId]),
		[fetchActiveIncidentsForServices],
	);

	return { fetchActiveIncidentsForServices, fetchActiveIncidentsForService };
};

export const useActiveIncidents = (): ActiveIncidentsState => {
	const [activeIncidents] = useActiveIncidentsStore();
	return activeIncidents;
};

export const useActiveIncidentsForService = (serviceId: string): ServiceDataLoadingState => {
	const [activeIncidents] = useActiveIncidentsStore();
	return activeIncidents[serviceId] ?? emptyServiceDataState;
};
